// src/components/ResultItem.js
import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getCaseBySearchId, updateResultReviewStatus } from '../services/caseService';
import { createPortal } from 'react-dom';
import { getDataSource, shouldShowSourceLink, getSourceUrl } from '../utils/dataSources';
import DataSourceBadge from './DataSourceBadge';

// Dedicated modal component with guaranteed centering
const ConfirmationModal = ({ show, title, message, onConfirm, onCancel, isLoading, t }) => {
  // Prevent body scrolling when modal is open
  useEffect(() => {
    if (show) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [show]);

  if (!show) return null;

  return createPortal(
    <div 
      className="fixed inset-0 bg-black/50 flex justify-center items-center z-50"
      onClick={(e) => e.target === e.currentTarget && onCancel()}
    >
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-[90%] max-w-md shadow-lg">
        <h4 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
          {title}
        </h4>
        
        <p className="text-gray-600 dark:text-gray-300 mb-6 text-sm leading-relaxed">
          {message}
        </p>
        
        <div className="flex justify-end gap-3">
          <button 
            onClick={onCancel}
            className="px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-md text-sm font-medium transition-colors"
          >
            {t('common.no')}
          </button>
          
          <button 
            onClick={onConfirm}
            disabled={isLoading}
            className={`px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md text-sm font-medium transition-colors ${
              isLoading ? 'opacity-70 cursor-not-allowed' : ''
            }`}
          >
            {t('common.yes')}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

const ResultItem = ({ result, searchTerm, t, searchId, onReviewStatusChange }) => {
  const { currentUser, currentOrg } = useAuth();
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  
  // Helper functions for confidence level styling
  const getConfidenceClass = (level) => {
    switch (level) {
      case "Very High": return "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400";
      case "High": return "bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400";
      case "Medium": return "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400";
      case "Low": return "bg-gray-100 dark:bg-gray-800/60 text-gray-700 dark:text-gray-400";
      default: return "bg-gray-100 dark:bg-gray-800/60 text-gray-700 dark:text-gray-400";
    }
  };
  
  const getConfidenceIcon = (level) => {
    switch (level) {
      case "Very High": return "fa-solid fa-circle-exclamation";
      case "High": return "fa-solid fa-triangle-exclamation";
      case "Medium": return "fa-solid fa-circle-info";
      case "Low": return "fa-solid fa-info";
      default: return "fa-solid fa-info";
    }
  };

  // Get border color based on confidence level
  const getBorderClass = (level) => {
    switch (level) {
      case "Very High": return "border-l-4 border-red-600 dark:border-red-700";
      case "High": return "border-l-4 border-yellow-500 dark:border-yellow-600";
      case "Medium": return "border-l-4 border-blue-600 dark:border-blue-700";
      case "Low": return "border-l-4 border-gray-500 dark:border-gray-600";
      default: return "border-l-4 border-gray-500 dark:border-gray-600";
    }
  };

  // Get score text color based on confidence level
  const getScoreClass = (level) => {
    switch (level) {
      case "Very High": return "text-red-600 dark:text-red-400";
      case "High": return "text-yellow-600 dark:text-yellow-400";
      case "Medium": return "text-blue-600 dark:text-blue-400";
      case "Low": return "text-gray-600 dark:text-gray-400";
      default: return "text-gray-600 dark:text-gray-400";
    }
  };

  // Render the score breakdown section
  const renderScoreBreakdown = () => {
    return (
      <details className="mt-4 group">
        <summary className="text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer">
          {t('results.scoreBreakdown')}
        </summary>
        <div className="mt-2 p-4 bg-white dark:bg-gray-800/90 rounded-sm shadow-sm dark:shadow-gray-900/40">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <div className="flex flex-col">
              <span className="text-xs text-gray-500 dark:text-gray-400">{t('results.tokenMatch')}:</span>
              <span className="text-sm font-medium text-gray-800 dark:text-gray-200">{Math.round(result.tokenMatchScore)}/50</span>
            </div>
            <div className="flex flex-col">
              <span className="text-xs text-gray-500 dark:text-gray-400">{t('results.positional')}:</span>
              <span className="text-sm font-medium text-gray-800 dark:text-gray-200">{Math.round(result.positionalScore)}/25</span>
            </div>
            <div className="flex flex-col">
              <span className="text-xs text-gray-500 dark:text-gray-400">{t('results.transliteration')}:</span>
              <span className="text-sm font-medium text-gray-800 dark:text-gray-200">{Math.round(result.transliterationScore)}/25</span>
            </div>
            {result.distributedBoost > 0 && (
              <div className="flex flex-col">
                <span className="text-xs text-gray-500 dark:text-gray-400">{t('results.distributedMatchBoost')}:</span>
                <span className="text-sm font-medium text-gray-800 dark:text-gray-200">+{result.distributedBoost}</span>
              </div>
            )}
            {result.culturalPattern && (
              <div className="flex flex-col col-span-2">
                <span className="text-xs text-gray-500 dark:text-gray-400">{t('results.culturalPattern')}:</span>
                <span className="text-sm font-medium text-gray-800 dark:text-gray-200">{result.culturalPattern}</span>
              </div>
            )}
          </div>
        </div>
      </details>
    );
  };
  
  // Handle confirm or dismiss
  const handleReviewUpdate = async (status) => {
    if (status === 'dismissed') {
      // Show confirmation dialog for dismiss
      setConfirmAction('dismiss');
      setShowConfirmModal(true);
    } else if (status === 'confirmed') {
      // Handle confirm directly
      updateReviewStatus(status);
    } else if (status === 'pending') {
      // Reset to pending without confirmation
      updateReviewStatus(status);
    }
  };
  
  // Update the review status
  const updateReviewStatus = async (status) => {
    if (!searchId || !currentUser || !currentOrg) return;
    
    setIsUpdating(true);
    
    try {
      // First, get the case associated with this search
      const caseData = await getCaseBySearchId(searchId, currentOrg.id);
      
      if (!caseData) {
        console.error('No case found for this search');
        return;
      }
      
      // Update the review status in the case
      const success = await updateResultReviewStatus(
        caseData.id,
        result.id,
        {
          review_status: status,
          review_notes: ''
        },
        currentOrg.id,
        currentUser.uid
      );
      
      if (success) {
        // Update the local result state to reflect the change immediately
        const prevStatus = result.review_status;
        result.review_status = status;
        result.review_timestamp = new Date().toISOString();
        result.reviewer_userID = currentUser.uid;
        
        // Notify parent component of the status change
        if (typeof onReviewStatusChange === 'function') {
          onReviewStatusChange(result.id, prevStatus, status);
        }
      }
    } catch (error) {
      console.error('Error updating review status:', error);
    } finally {
      setIsUpdating(false);
      setShowConfirmModal(false);
    }
  };
  
  // Get a class based on review status
  const getReviewStatusClass = () => {
    switch (result.review_status) {
      case 'confirmed': return 'bg-green-50 dark:bg-green-900/10';
      case 'dismissed': return 'bg-gray-50 dark:bg-gray-800/30 opacity-70';
      default: return '';
    }
  };
  
  const confidenceClass = getConfidenceClass(result.confidenceLevel);
  const borderClass = getBorderClass(result.confidenceLevel);
  const scoreClass = getScoreClass(result.confidenceLevel);
  const confidenceIcon = getConfidenceIcon(result.confidenceLevel);
  
  // Map confidence level to translation key
  const getConfidenceTranslationKey = (level) => {
    switch(level) {
      case "Very High": return 'search.veryHighRiskMatch';
      case "High": return 'search.highRiskMatch';
      case "Medium": return 'search.mediumRiskMatch';
      case "Low": return 'search.lowRiskMatch';
      default: return 'search.riskMatch';
    }
  };

  // Helper to get the primary data source for the result
  const getPrimaryDataSource = () => {
    if (!result.docData?.source) return null;
    
    // Handle different formats of data source information
    const sourceId = typeof result.docData.source === 'string' 
      ? result.docData.source.toUpperCase() 
      : null;
      
    return sourceId ? getDataSource(sourceId) : null;
  };

  return (
    <li className={`p-5 bg-gray-50 dark:bg-gray-900 rounded-lg shadow-md ring-1 ring-gray-200/50 dark:ring-gray-800/50 ${borderClass} ${getReviewStatusClass()} relative`}>
      {/* Confidence indicator */}
      <div className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium mb-3 ${confidenceClass}`}>
        <i className={confidenceIcon} aria-hidden="true"></i>
        <span className="ml-2">{t(getConfidenceTranslationKey(result.confidenceLevel))}</span>
      </div>
      
      {/* Review status badge */}
      {result.review_status && result.review_status !== 'pending' && (
        <div className="absolute top-4 right-4 flex items-center">
          <button 
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 mr-2 p-1"
            onClick={() => handleReviewUpdate('pending')}
            title={t('results.undoReview')}
            aria-label={t('results.undoReview')}
            disabled={isUpdating}
          >
            <i className="fa-solid fa-rotate-left"></i>
          </button>
          <div className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
            result.review_status === 'confirmed' 
              ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' 
              : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
          }`}>
            {result.review_status === 'confirmed' ? t('results.confirmed') : t('results.dismissed')}
          </div>
        </div>
      )}
      
      {/* Name with score */}
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-xl font-bold text-gray-900 dark:text-white">{result.fullName}</h3>
        <span className={`text-xl font-bold ${scoreClass}`}>
          {result.unifiedScore}%
        </span>
      </div>
      
      {/* Data sources tags */}
      <div className="flex flex-wrap gap-2 mb-3">
        {/* Display the primary source as a badge */}
        {result.docData?.source && (
          <DataSourceBadge 
            sourceId={result.docData.source} 
            size="md" 
            showIcon={true} 
            fullName={true}
          />
        )}
        
        {/* Maintain compatibility with old format - display additional data sources as badges */}
        {result.docData?.dataSources && Array.isArray(result.docData.dataSources) && result.docData.dataSources.map((source, index) => {
          // For backward compatibility, map old source names to display names
          const sourceNameMap = {
            "financial-sanctions": t('results.financialSanctions'),
            "travel-bans": t('results.travelBans'),
            "un-sanctions": t('results.unSanctions')
          };
          
          return (
            <span key={index} className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300">
              {sourceNameMap[source] || source}
            </span>
          );
        })}
      </div>
      
      {/* Cross-name match explanation */}
      {result.isDistributedMatch && (
        <div className="p-3 bg-blue-50 dark:bg-blue-900/10 rounded-md border border-blue-100 dark:border-blue-800/30 mb-4">
          <div className="font-medium text-blue-800 dark:text-blue-300 text-sm mb-1">{t('results.distributedNameMatch')}</div>
          <div className="text-blue-700 dark:text-blue-200 text-sm">
            {t('results.crossMatchExplanation', { term: searchTerm })}
            <ul className="mt-2 space-y-1">
              {Object.entries(result.distributionInfo || {}).map(([nameIndex, tokens], idx) => {
                if (!tokens || tokens.length === 0) return null;
                const nameData = result.docData.names[nameIndex];
                const nameText = nameData.wholeName || `${nameData.firstName || ''} ${nameData.middleName || ''} ${nameData.lastName || ''}`.trim();
                
                return (
                  <li key={idx} className="text-xs">
                    <span className="font-medium">{nameText}</span> {t('results.matchedVariants')}: 
                    <span className="bg-blue-100 dark:bg-blue-800/50 px-1 rounded ml-1">{tokens.join(', ')}</span>
                    {result.fuzzyMatches && result.fuzzyMatches.includes(nameIndex) && ` (${t('results.fuzzy')})`}
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      )}
      
      {/* Score breakdown */}
      {renderScoreBreakdown()}
      
      {/* Basic entity details */}
      {result.function && (
        <div className="mt-3">
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('results.function')}</p>
          <p className="text-sm text-gray-800 dark:text-gray-200">{result.function}</p>
        </div>
      )}
      
      {result.docData?.citizenships && Array.isArray(result.docData.citizenships) && result.docData.citizenships.length > 0 ? (
        <div className="mt-3">
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('results.citizenship')}</p>
          <p className="text-sm text-gray-800 dark:text-gray-200">
            {result.docData.citizenships.map(citizenship => {
              // Handle object format
              if (typeof citizenship === 'object' && citizenship.countryDescription) {
                // Convert to title case (first letter capital, rest lowercase)
                return citizenship.countryDescription.charAt(0).toUpperCase() + 
                       citizenship.countryDescription.slice(1).toLowerCase();
              }
              // Handle string format
              return typeof citizenship === 'string' ? citizenship : '';
            }).filter(Boolean).join(', ')}
          </p>
        </div>
      ) : null}
      
      {/* Alternative names */}
      {result.docData?.names && result.docData.names.length > 1 && (
        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
          <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{t('results.alternativeNames')}:</div>
          <ul className="space-y-1">
            {result.docData.names
              .filter((name, idx) => idx !== result.nameIndex) // Filter out the primary name
              .map((name, idx) => {
                const constructedName = `${name.title || ''} ${name.firstName || ''} ${name.middleName || ''} ${name.lastName || ''}`.trim();
                const altName = name.wholeName ? name.wholeName : constructedName;
                
                return altName ? (
                  <li key={idx} className="flex justify-between items-center p-2.5 bg-white dark:bg-gray-800/90 rounded-sm text-sm shadow-sm dark:shadow-gray-900/40">
                    <div className="text-gray-800 dark:text-gray-200">
                      <span>{altName}</span>
                    </div>
                    
                    {/* For distributed matches, highlight which tokens matched this name */}
                    {result.isDistributedMatch && result.distributionInfo && result.distributionInfo[idx + (idx >= result.nameIndex ? 1 : 0)] && (
                      <div className="text-xs text-blue-600 dark:text-blue-400">
                        <span className="bg-blue-100 dark:bg-blue-900/30 px-1.5 py-0.5 rounded-sm">
                          ✓ {t('results.matchedVariants')}: {result.distributionInfo[idx + (idx >= result.nameIndex ? 1 : 0)].join(', ')}
                        </span>
                      </div>
                    )}
                  </li>
                ) : null;
              })
              .filter(Boolean)
            }
          </ul>
        </div>
      )}
      
      {/* EU Sanctions link */}
      {result.docData?.logicalId && (
        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
          {(() => {
            const dataSource = getPrimaryDataSource();
            const sourceId = result.docData?.source?.toUpperCase();
            
            // If we don't have a valid data source or shouldn't show link, don't render anything
            if (!dataSource || !sourceId || !shouldShowSourceLink(sourceId, result)) {
              return null;
            }
            
            // Get the URL with proper identifier
            const href = getSourceUrl(sourceId, result);
            
            return (
              <a 
                href={href}
                target="_blank" 
                rel="noopener noreferrer"
                className={`inline-flex items-center px-3 py-2 text-sm font-medium ${dataSource.buttonColor} rounded-md transition-colors`}
              >
                <i className={`${dataSource.icon || 'fa-solid fa-external-link'} mr-2`}></i>
                <span>{dataSource.linkText}</span>
              </a>
            );
          })()}
        </div>
      )}
      
      {/* Document ID and review actions in the same row */}
      <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div className="text-xs text-gray-500 dark:text-gray-400">{t('results.documentId')}: <span className="font-mono">{result.id}</span></div>
        
        {/* Debug: Check why review actions might not show */}
        {console.log('Review actions check:', { 
          resultId: result.id, 
          searchId: searchId, 
          review_status: result.review_status,
          shouldShow: searchId && result.review_status === 'pending',
          allProps: Object.keys(result)
        })}
        
        {/* Review action buttons - only show if not already reviewed */}
        {(searchId && (result.review_status === 'pending' || result.review_status === undefined)) && (
          <div className="flex gap-2">
            <button 
              className="px-3 py-1.5 text-sm text-red-600 hover:text-white bg-white hover:bg-red-600 border border-red-200 hover:border-red-600 rounded-md transition-colors dark:text-red-400 dark:bg-transparent dark:border-red-700 dark:hover:bg-red-700 dark:hover:text-white"
              onClick={() => handleReviewUpdate('dismissed')}
              disabled={isUpdating}
            >
              {t('results.dismiss')}
            </button>
            <button 
              className="px-3 py-1.5 text-sm text-green-600 hover:text-white bg-white hover:bg-green-600 border border-green-200 hover:border-green-600 rounded-md transition-colors dark:text-green-400 dark:bg-transparent dark:border-green-700 dark:hover:bg-green-700 dark:hover:text-white"
              onClick={() => handleReviewUpdate('confirmed')}
              disabled={isUpdating}
            >
              {t('results.confirmMatch')}
            </button>
          </div>
        )}
      </div>
      
      {/* Confirmation modal with guaranteed centering */}
      <ConfirmationModal
        show={showConfirmModal}
        title={t('results.confirmDismiss')}
        message={t('results.dismissExplanation')}
        onConfirm={() => updateReviewStatus('dismissed')}
        onCancel={() => setShowConfirmModal(false)}
        isLoading={isUpdating}
        t={t}
      />
    </li>
  );
};

export default ResultItem;
